import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { checkPermission } from '@/utils/permission';
import DynamicTable from '@/components/DynamicTable';
import { renderAppointmentName, isValidateTime, isValidDate } from '@/utils/helpers';
import { Button, Col, Divider, Modal, Row, Space, Tooltip } from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { fetchCustomer } from '@/redux/actions/customerActions';
import { removeAppointment, getSpecificAppointment } from '@/redux/actions/appointmentActions';
import HtmlParser from 'react-html-parser';
import AppointmentStatusButton from '@/components/appointments/AppointmentStatusButton';
import AppointmentDetail from '@/components/appointments/AppointmentDetail';
import AppointmentForm from '@/components/appointments/AppointmentForm';
import AppointmentUpdateStatus from '@/components/appointments/AppointmentUpdateStatus';
import AppointmentStaffCell from '@/components/appointments/AppointmentStaffCell';
import AppointmentServicesCell from '@/components/appointments/AppointmentServicesCell';

const CustomerAppointments = (props) => {
    const { appointments = [], customer } = props;

    const dispatch = useDispatch();
    const { id } = useParams();

    const allowCreate = checkPermission('appointment', 'create');
    const allowDetail = checkPermission('appointment', 'detail');
    const allowEdit = checkPermission('appointment', 'update');

    const [isLoading, setIsLoading] = useState(false);

    const [isOpenDetail, setIsOpenDetail] = useState(false);
    const [isOpenForm, setIsOpenForm] = useState(false);
    const [isOpenStatusForm, setIsOpenStatusForm] = useState(false);

    const [currentItem, setCurrentItem] = useState(null);

    const handleReloadData = async () => {
        const customerId = customer?.id || id;
        if (!customerId) return;

        try {
            setIsLoading(true);
            await dispatch(fetchCustomer(customerId));
        } catch (error) {
            console.error('Failed to reload data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const toggleOpenDetail = (open, item = null) => {
        setIsOpenDetail(open);
        if (!open) {
            setCurrentItem(null);
            return;
        }
        setCurrentItem(item);
    };

    const toggleOpenForm = (open, item = null) => {
        setIsOpenForm(open);
        if (!open) {
            setCurrentItem(null);
            return;
        }
        setCurrentItem(item);
    };

    const toggleOpenUpdateStatusForm = (open, item = null) => {
        setIsOpenStatusForm(open);
        if (!open) {
            setCurrentItem(null);
            return;
        }
        setCurrentItem(item);
    };

    const onCreate = () => {
        toggleOpenForm(true, null);
    };

    const onDetail = async (appointmentId) => {
        try {
            setIsLoading(true);
            const res = await dispatch(getSpecificAppointment(appointmentId));
            toggleOpenDetail(true, res);
        } catch (error) {
            console.error('Lỗi khi tải chi tiết lịch hẹn:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const onEdit = async (appointmentId) => {
        try {
            setIsLoading(true);
            const res = await dispatch(getSpecificAppointment(appointmentId));
            toggleOpenForm(true, res);
        } catch (error) {
            console.error('Lỗi khi mở form edit lịch hẹn:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const onOpenStatusForm = async (appointmentId) => {
        try {
            setIsLoading(true);
            const res = await dispatch(getSpecificAppointment(appointmentId));
            toggleOpenUpdateStatusForm(true, res);
        } catch (error) {
            console.error('Lỗi khi mở form cập nhật trạng thái lịch hẹn:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const onDelete = (appointmentId) => {
        Modal.confirm({
            title: 'Cảnh báo',
            content: 'Bạn chắc chắn muốn xoá bản ghi này?',
            okText: 'OK',
            cancelText: 'Huỷ',
            onOk: async () => {
                try {
                    setIsLoading(true);
                    await dispatch(removeAppointment([appointmentId]));
                    await handleReloadData();
                } catch (error) {
                    console.error('Lỗi khi xóa lịch hẹn:', error);
                } finally {
                    setIsLoading(false);
                }
            }
        });
    };

    const columns = [
        {
            title: 'Kiểu lịch hẹn',
            dataIndex: 'name',
            key: 'name',
            width: '120px',
            render: (text, record) => {
                if (allowDetail) {
                    return (
                        <Tooltip title="Xem chi tiết lịch hẹn" placement="right">
                            <div className='item-action-btn' onClick={() => onDetail(record.id)}>
                                <strong>{renderAppointmentName(text)}</strong>
                            </div>
                        </Tooltip>
                    );
                }

                return <strong>{renderAppointmentName(text)}</strong>;
            }
        },
        {
            title: 'Ngày giờ',
            dataIndex: 'date',
            key: 'date',
            width: '150px',
            render: (_, record) => (
                <div>{isValidDate(record.scheduled_at, false)} {isValidateTime(record.scheduled_at)}</div>
            )
        },
        {
            title: 'Phụ trách buổi hẹn',
            key: 'staffs',
            width: 280,
            render: (_, record) => (
                <AppointmentStaffCell record={record} collapseSingleDoctor />
            ),
        },
        {
            title: 'Cơ sở',
            dataIndex: ['branch', 'name'],
            key: 'branch',
            width: 150,
            render: (value) => value || '---'
        },
        {
            title: 'Ghi chú',
            dataIndex: 'note',
            key: 'note',
            width: '250px',
            render: (text) => <>{HtmlParser(text)}</>
        },
        {
            title: 'Trạng thái',
            dataIndex: 'status',
            key: 'status',
            width: '100px',
            render: (_, record) => (
                <AppointmentStatusButton
                    record={record}
                    onClick={() => onOpenStatusForm(record.id)}
                />
            )
        },
        {
            title: 'Dịch vụ',
            key: 'services',
            width: 320,
            render: (_, record) => (
                <AppointmentServicesCell services={record?.services || []} />
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 180,
            align: 'center',
            render: (_, record) => (
                <Space direction="horizontal" justify="center" size={6} align="center">
                    <Button
                        type="primary"
                        size="small"
                        onClick={() => onEdit(record.id)}
                        disabled={!allowEdit}
                    >
                        Sửa
                    </Button>

                    <Divider type="vertical" />

                    <Button
                        danger
                        size="small"
                        onClick={() => onDelete(record.id)}
                        disabled={!allowEdit}
                    >
                        Xóa
                    </Button>
                </Space>
            )
        }
    ];

    return (
        <div>
            <div className='mt-4 mb-4'>
                <Row gutter={8}>
                    <Col>
                        <Space size={8}>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={onCreate}
                                disabled={!allowCreate}
                            >
                                Tạo lịch hẹn
                            </Button>

                            <Button
                                onClick={handleReloadData}
                                icon={<ReloadOutlined />}
                                loading={isLoading}
                            >
                                Refresh
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </div>

            <div className='has-custom-column-table-btn'>
                <DynamicTable
                    dataSource={appointments || []}
                    columns={columns}
                    isLoading={isLoading}
                    pagination={false}
                    rowSelection={false}
                    moduleKey="customer_appointment_list"
                    buttonStyle={{ top: 6 }}
                />
            </div>

            <AppointmentDetail
                visible={isOpenDetail}
                currentItem={currentItem}
                onCancel={() => toggleOpenDetail(false)}
            />
            <AppointmentForm
                visible={isOpenForm}
                customerId={customer?.id || currentItem?.customer_id}
                currentItem={currentItem}
                reloadData={handleReloadData}
                onCancel={() => toggleOpenForm(false)}
            />
            <AppointmentUpdateStatus
                visible={isOpenStatusForm}
                currentItem={currentItem}
                reloadData={handleReloadData}
                onCancel={() => toggleOpenUpdateStatusForm(false)}
            />
        </div>
    );
};

export default CustomerAppointments;
